import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { SearchForm } from '@playbooks/molecules/forms';
import { AlgoliaService } from '@playbooks/services';
import { Button } from '@playbooks/ui/components/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';
import { InputGroupAddon } from '@playbooks/ui/components/input-group';

import { appParams } from 'api';
import { SearchResultsPreview } from 'components/search/search-results-preview';
import { useQuery } from 'hooks';

const SearchModal = ({ tableName, tableNames = [], placeholder, open, setOpen, className = '' }) => {
	const [query, setQuery] = useState('');
	const [results, setResults] = useState([]);
	const [params, setParams] = useState({ ...appParams, pageSize: 5 });
	const [meta, setMeta] = useState({ page: 0, pageSize: 0, totalRecords: 0 });
	const ref = useRef(null);
	const router = useRouter();

	// Computed
	const client = new AlgoliaService({ tableName });

	// Hooks
	useEffect(() => {
		if (query.length >= 3) searchData();
	}, [query, params, tableName]);
	useEffect(() => {
		if (open) onClose();
	}, [router.pathname]);

	// Actions
	const [searchData, loading] = useQuery(async () => {
		const response = await client.queryIndex(query, params);
		setResults(response.data);
		setMeta(response.meta);
	});

	// Methods
	const onSubmit = e => {
		e.preventDefault();
	};
	const onClose = () => {
		if (ref.current) ref.current.blur();
		onClear();
		setOpen(false);
	};
	const onClear = () => {
		setQuery('');
		if (ref.current) ref.current.value = '';
		setResults([]);
	};

	// Render
	return (
		<Dialog open={open} onOpenChange={nextOpen => (nextOpen ? setOpen(true) : onClose())}>
			<DialogContent
				showCloseButton={false}
				initialFocus={ref}
				className='max-h-[min(42rem,calc(100vh-2rem))] gap-0 overflow-hidden p-0 sm:max-w-3xl'>
				<DialogHeader className='sr-only'>
					<DialogTitle>Search the Playbooks blog</DialogTitle>
					<DialogDescription>Search articles and select a result to open it.</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit}>
					<SearchForm
						ref={ref}
						delay={300}
						query={query}
						setQuery={setQuery}
						placeholder={placeholder}
						elements={{
							inputAppend: (
								<InputGroupAddon className='mx-2 my-1.5'>
									<Button
										type='button'
										variant='outline'
										size='xs'
										onClick={onClear}
										className='text-xs text-foreground-body'>
										{open ? 'ESC' : '⌘K'}
									</Button>
								</InputGroupAddon>
							),
						}}
						className={className}
					/>
				</form>
				<div className='min-h-0 overflow-y-auto border-t'>
					<SearchResultsPreview
						query={query}
						hits={results}
						loading={loading}
						tableName={tableName}
						tableNames={tableNames}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
};
export { SearchModal };
